import { Transition } from "@headlessui/react";

import { useState, useEffect } from "react";

import { useAppDispatch, useAppSelector, useMediaQuery } from "../../../hooks";
import {
  reset,
  selectRole,
  selectUser,
} from "../../../states/slices/authReducer";

import { useLocation, useNavigate } from "react-router-dom";

// import { FaHome } from "react-icons/fa";

import { Link } from "react-router-dom";
import { Share, LogOut } from "lucide-react";

import { UserType } from "../../../utils-func/enums";
import { CUSTOMARY_LOGO } from "../../../assets";
import { LawyerSidebarRoute } from "../../../features/modules/lawyers/routes/sidebarRoutes";
import WarningPromptModal from "../../../features/modules/lawyers/internals/case-folder/modals/WarningPromptModal";
import { PrivateIndividualSidebarRoute } from "../../../features/modules/private-individuals/routes/sidebarRoutes";
import { Registrar1AssessmentSidebarRoute } from "../../../features/modules/registrar1-assessment/routes/sidebarRoutes";
import { Registrar1PaymentSidebarRoute } from "../../../features/modules/registrar1-payment/routes/sidebarRoutes";
import { Registrar2SidebarRoute } from "../../../features/modules/registrar2/routes/sidebarRoutes";
import { PresidentRoute } from "../../../features/modules/president/routes/route";
import { PresidentSidebarRoute } from "../../../features/modules/president/routes/sidebarRoutes";
import { PanelSidebarRoute } from "../../../features/modules/panel/routes/sidebarRoutes";
import { BaliffSidebarRoute } from "../../../features/modules/baliff/routes/sidebarRoutes";
import { PoliceSidebarRoute } from "../../../features/modules/police/routes/sidebarRoutes";
import { AdminSidebarRoute } from "../../../features/modules/admin/routes/sidebarRoutes";

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  setOpen: any;
}

const Sidebar = (props: SidebarProps) => {
  const { onClose } = props;
  const user = useAppSelector(selectUser);
  const [logoutPrompt, setLogoutPrompt] = useState(false);

  const dispatch = useAppDispatch();

  const isMobileView = useMediaQuery("(max-width: 640px)");
  const isTabletView = useMediaQuery("(max-width: 840px)");

  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const role = useAppSelector(selectRole);

  const getSidebarRoutes = () => {
    switch (role) {
      case "lawyer":
        return LawyerSidebarRoute;
      case "panel":
        return PanelSidebarRoute;
      case "individual":
        return PrivateIndividualSidebarRoute;
      case "registrar1assessment":
        return Registrar1AssessmentSidebarRoute;
      case "registrar1payment":
        return Registrar1PaymentSidebarRoute;
      case "registrar2":
        return Registrar2SidebarRoute;
      case "president":
        return PresidentSidebarRoute;
      case "police":
        return PoliceSidebarRoute;
      case "baliff":
        return BaliffSidebarRoute;
      case "admin":
        return AdminSidebarRoute;

      default:
        return [];
    }
  };

  const handleLogout = () => {
    dispatch(reset());
    navigate("/login");
  };

  const SidebarContent = () => (
    <div className="w-full max-w-[270px] flex-none py-5 shadow-sm h-screen bg-[#F7F8F9] border-r border-[#E9EAEB] flex flex-col justify-between overflow-y-auto">
      <div>
        <div className="px-6 flex flex-col gap-y-3 items-start">
          <img src={CUSTOMARY_LOGO} className="w-[60px] h-[60px]" alt="Logo" />
          <span className="text-[#464255] font-semibold text-xs leading-5">
            Enugu State Customary
            <br />
            Court of Appeal
          </span>
        </div>
        <div className="mt-3 w-full flex flex-col gap-y-2">
          {getSidebarRoutes()?.map((item) => {
            const isActive = pathname.startsWith(item.path);
            return (
              <div key={item.path} className="relative flex items-center">
                {isActive && (
                  <div className="absolute left-0 h-10 w-1 bg-[#00B074]" />
                )}
                <Link
                  to={item.path}
                  className={`flex-1 flex items-center gap-x-3 px-4 py-3 ml-6 mr-4 text-sm font-medium transition-all duration-200 rounded-md ${
                    isActive
                      ? "bg-[#E6F7F1] text-[#00B074]"
                      : "text-[#464255] hover:bg-gray-100"
                  }`}
                >
                  <img
                    src={isActive ? item.activeIcon : item.inactiveIcon}
                    alt={item.title}
                    className="w-5 h-5"
                  />
                  <span className="leading-5 whitespace-normal break-words">
                    {item.title}
                  </span>
                </Link>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-6 mb-2">
        <button
          onClick={() => setLogoutPrompt(true)}
          className="flex items-center gap-x-3 text-[#464255] text-sm font-medium hover:text-red-500 transition-colors duration-200"
        >
          <LogOut className="w-5 h-5" />
          <span>Log Out</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {isMobileView || isTabletView ? (
        <Transition
          as="div"
          className="fixed z-50 h-full w-80 flex-none bg-brand-light lg:static"
          enter="transition-all ease-in duration-300"
          enterFrom="transform -translate-x-full"
          enterTo="transform -translate-x-0"
          leave="transition-all ease-out duration-300"
          leaveFrom="transform -translate-x-0"
          leaveTo="transform -translate-x-full"
          show={props.open}
        >
          <SidebarContent />
        </Transition>
      ) : (
        <SidebarContent />
      )}
      <WarningPromptModal
        isOpen={logoutPrompt}
        onClose={() => setLogoutPrompt(false)}
        message="Are you sure you want log out?"
        onClick={handleLogout}
      />
    </>
  );
};

export default Sidebar;
